// exp_PORTFOLIO-58018_file
import { FC, MouseEvent, useCallback, useMemo } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardStep,
    createCampaignCreationWizardBreadcrumbClickHandler,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE, DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';

import vrPackageCloseButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vr_package/vr_package_close_button_click';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';

import styles from 'pages/VrCampaignBuilderPage/VrCampaignBuilderPage.module.less';

import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { VacancyLink } from 'components/common/VacancyLink/VacancyLink';
import { Header } from 'components/Header/Header';
import { VrCampaignBuilder } from 'components/VrCampaignBuilder/VrCampaignBuilder';

import { selectVrCampaignVacancy } from 'store/models/vrCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'CampaignCreationWizard.PromotionTypeStep.vr.title',
    breadcrumbs: {
        main: 'CampaignCreationWizard.breadcrumbs.main',
        creation: 'CampaignCreationWizard.breadcrumbs.creation',
        promotionType: 'CampaignCreationWizard.breadcrumbs.promotionType',
    },
};

export const VrCampaignBuilderPage: FC = () => {
    const { vacancyId, vacancyName } = useSelector(selectVrCampaignVacancy);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const isWizardFlowEnabled = Boolean(vacancyId);

    const openWizardBackAlert = useCallback(
        (activeStep: CampaignCreationWizardStep) => {
            openBackAlert(() => {
                vrPackageCloseButtonClick();
                goToWizardStep(activeStep, {
                    vacancyId,
                    vacancyName: vacancyName ?? undefined,
                    productType: CampaignProductType.Vr,
                });
            });
        },
        [goToWizardStep, openBackAlert, vacancyId, vacancyName]
    );

    const additionalContent = vacancyId ? (
        <VacancyLink dataQa="header-vacancy-link" vacancyId={vacancyId} vacancyName={vacancyName} />
    ) : null;

    const breadcrumbs = useMemo(() => {
        if (isWizardFlowEnabled) {
            return [
                {
                    to: DASHBOARD_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.main),
                    onClick: (event: MouseEvent) => {
                        event.preventDefault();
                        openBackAlert(() => {
                            vrPackageCloseButtonClick();
                            dispatch(push(DASHBOARD_PAGE_ROUTE));
                        });
                    },
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.creation),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionObject
                    ),
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.promotionType),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionType
                    ),
                },
                { to: '#', children: trls(TrlKeys.title) },
            ];
        }

        return [
            {
                to: CREATE_MENU_PAGE_ROUTE,
                children: trls(TrlKeys.breadcrumbs.main),
                onClick: () => vrPackageCloseButtonClick(),
            },
            { to: '#', children: trls(TrlKeys.title) },
        ];
    }, [dispatch, isWizardFlowEnabled, openBackAlert, openWizardBackAlert, trls]);

    return (
        <div className={styles.wrapper}>
            <Header />
            <PageContentHeader
                breadcrumbsConfig={breadcrumbs}
                title={trls(TrlKeys.title)}
                additionalContent={additionalContent}
                vSpacingBottom={24}
            />
            <VrCampaignBuilder />
            {isWizardFlowEnabled && backAlert}
        </div>
    );
};
